import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, MapPin, Calendar, Clock, Truck, User, Phone, Mail, Package,
  Download, CheckCircle2, Trash2, ExternalLink, Sparkles,
  Loader2, AlertCircle, Route as RouteIcon, GripVertical, Save, Undo2,
  Printer, QrCode,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

// Fix default Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const STATUS_META = {
  Draft:      { label: 'Borrador',   cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  Published:  { label: 'Publicada',  cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  Completed:  { label: 'Completada', cls: 'bg-green-100 text-green-800 border-green-200' },
};

const SHIFT_LABELS = {
  'mañana': 'Mañana (07:00–14:00)',
  'tarde':  'Tarde (14:00–21:00)',
  'noche':  'Noche (23:00–06:00)',
};

const fmtDateES = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
};

const numberedIcon = (n, color = '#374B67') => L.divIcon({
  className: 'route-stop-marker',
  html: `<div style="
    background:${color};color:#fff;
    width:30px;height:30px;border-radius:50%;
    display:flex;align-items:center;justify-content:center;
    font-weight:700;font-size:13px;
    box-shadow:0 2px 6px rgba(0,0,0,0.3);
    border:2px solid #fff;
  ">${n}</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

// ────────────────────────────────────────────────────────────────────────────
// Sortable row component
// ────────────────────────────────────────────────────────────────────────────
function SortableStopRow({ stop, index, total, dragDisabled }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: stop.id, disabled: dragDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#F1F5F9' : undefined,
  };

  const isFirst = index === 0;
  const isLast = index === total - 1;
  const markerColor = isFirst ? '#009940' : (isLast ? '#DC2626' : '#374B67');

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="hover:bg-slate-50/60 group border-b border-slate-100"
      data-testid={`route-stop-${index}`}
    >
      <td className="px-2 py-3 align-top w-10 text-center">
        <button
          {...attributes}
          {...listeners}
          disabled={dragDisabled}
          className={`inline-flex items-center justify-center w-7 h-7 rounded text-slate-400
            ${dragDisabled
              ? 'cursor-not-allowed opacity-40'
              : 'hover:bg-slate-100 hover:text-slate-700 cursor-grab active:cursor-grabbing'}`}
          title={dragDisabled ? 'Sólo en estado Borrador' : 'Arrastra para reordenar'}
          data-testid={`route-drag-${index}`}
          aria-label="Reordenar"
        >
          <GripVertical size={16} />
        </button>
      </td>
      <td className="px-2 py-3 align-top w-12">
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-semibold"
          style={{ background: markerColor }}
        >
          {index + 1}
        </span>
      </td>
      <td className="px-3 py-3 align-top">
        <Link
          to={`/admin/solicitudes?ticket=${stop.ticket_code}`}
          className="font-mono text-xs text-[#374B67] hover:underline"
        >
          {stop.ticket_code}
        </Link>
      </td>
      <td className="px-3 py-3 align-top">
        <div className="text-slate-800 font-medium text-sm">
          {stop.nombre_completo || stop.nombre || '—'}
        </div>
        {stop.zona && <div className="text-xs text-slate-500">{stop.zona}</div>}
      </td>
      <td className="px-3 py-3 align-top text-xs text-slate-600">
        {stop.telefono && (
          <div className="flex items-center gap-1"><Phone size={11} /> {stop.telefono}</div>
        )}
        {stop.email && (
          <div className="flex items-center gap-1 mt-0.5 truncate max-w-[160px]" title={stop.email}>
            <Mail size={11} /> {stop.email}
          </div>
        )}
        {!stop.telefono && !stop.email && '—'}
      </td>
      <td className="px-3 py-3 align-top text-xs text-slate-700 max-w-[260px]">
        <div className="flex items-start gap-1">
          <MapPin size={11} className="mt-0.5 text-slate-400 flex-shrink-0" />
          <span className="break-words">{stop.direccion_completa || stop.direccion_aprox || '—'}</span>
        </div>
        {stop.latitude && stop.longitude && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${stop.latitude},${stop.longitude}&travelmode=driving`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-0.5 mt-1"
          >
            Ir en Maps <ExternalLink size={9} />
          </a>
        )}
      </td>
      <td className="px-3 py-3 align-top text-xs text-slate-700 max-w-[200px]">
        <div className="font-medium">{stop.tipo_enseres || '—'}</div>
        {stop.detalles && (
          <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{stop.detalles}</div>
        )}
      </td>
      <td className="px-3 py-3 align-top">
        <Badge variant="secondary" className="text-xs">{stop.estado || '—'}</Badge>
      </td>
    </tr>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────────
export default function AdminRutaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const [stops, setStops] = useState([]);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingPoster, setDownloadingPoster] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const dragDisabled = useMemo(
    () => (route?.status && route.status !== 'Draft'),
    [route?.status]
  );

  const orderChanged = useMemo(() => {
    if (stops.length !== originalOrder.length) return false;
    return stops.some((s, i) => s.id !== originalOrder[i]);
  }, [stops, originalOrder]);

  const fetchRoute = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/admin/routes/${id}`, { headers: authHeaders() });
      setRoute(resp.data);
      const fetched = resp.data.stops || [];
      setStops(fetched);
      setOriginalOrder(fetched.map(s => s.id));
    } catch (err) {
      const msg = err.response?.status === 404
        ? 'Ruta no encontrada'
        : (err.response?.data?.detail || 'Error al cargar la ruta');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoute(); /* eslint-disable-next-line */ }, [id]);

  // Render map whenever stops change
  useEffect(() => {
    if (!mapRef.current) return;
    const withCoords = stops.filter(s => s.latitude && s.longitude);
    if (mapInstance.current) {
      try { mapInstance.current.remove(); } catch { /* noop */ }
      mapInstance.current = null;
    }
    if (withCoords.length === 0) return;

    const m = L.map(mapRef.current, { zoomControl: true });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(m);

    const latlngs = withCoords.map(s => [s.latitude, s.longitude]);
    withCoords.forEach((s, i) => {
      const isFirst = i === 0;
      const isLast = i === withCoords.length - 1;
      const color = isFirst ? '#009940' : (isLast ? '#DC2626' : '#374B67');
      const marker = L.marker([s.latitude, s.longitude], { icon: numberedIcon(i + 1, color) }).addTo(m);
      marker.bindPopup(
        `<div style="font-family:Inter,sans-serif;min-width:200px">
          <p style="margin:0 0 4px 0;font-weight:600;color:#374B67">${s.ticket_code || ''}</p>
          <p style="margin:0 0 2px 0;font-size:13px">${s.nombre_completo || s.nombre || '—'}</p>
          <p style="margin:0;font-size:11px;color:#64748b">${s.direccion_completa || s.direccion_aprox || ''}</p>
        </div>`
      );
    });

    L.polyline(latlngs, { color: '#374B67', weight: 3, opacity: 0.7, dashArray: '6, 6' }).addTo(m);

    try { m.fitBounds(latlngs, { padding: [40, 40] }); } catch { m.setView(latlngs[0], 14); }
    setTimeout(() => m.invalidateSize(), 150);
    mapInstance.current = m;

    return () => {
      try { m.remove(); } catch { /* noop */ }
      mapInstance.current = null;
    };
  }, [stops]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setStops((items) => {
      const oldIdx = items.findIndex(i => i.id === active.id);
      const newIdx = items.findIndex(i => i.id === over.id);
      return arrayMove(items, oldIdx, newIdx);
    });
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      const newSequence = stops.map(s => s.id);
      const resp = await axios.patch(
        `${API}/admin/routes/${id}/sequence`,
        { sequence: newSequence },
        { headers: authHeaders() }
      );
      setRoute(prev => ({ ...prev, ...resp.data }));
      setOriginalOrder(newSequence);
      toast.success('Nuevo orden guardado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar el orden');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleResetOrder = () => {
    const byId = Object.fromEntries(stops.map(s => [s.id, s]));
    setStops(originalOrder.map(sid => byId[sid]).filter(Boolean));
  };

  const downloadBlob = async (urlPath, filename) => {
    const resp = await axios.get(`${API}${urlPath}`, {
      headers: authHeaders(),
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const safeName = (route?.name || 'ruta').replace(/[^a-zA-Z0-9_-]/g, '_');
      await downloadBlob(
        `/admin/routes/${id}/pdf`,
        `Ruta_${safeName}_${route?.date || ''}.pdf`
      );
      toast.success('PDF descargado');
    } catch {
      toast.error('No se pudo descargar el PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadPoster = async () => {
    setDownloadingPoster(true);
    try {
      const safeName = (route?.name || 'ruta').replace(/[^a-zA-Z0-9_-]/g, '_');
      await downloadBlob(
        `/admin/routes/${id}/qr-poster`,
        `QR_Ruta_${safeName}_${route?.date || ''}.pdf`
      );
      toast.success('Cartel QR descargado');
    } catch {
      toast.error('No se pudo descargar el cartel');
    } finally {
      setDownloadingPoster(false);
    }
  };

  const handlePublish = async () => {
    if (orderChanged) {
      if (!window.confirm('Hay cambios de orden sin guardar. ¿Publicar de todos modos? (los cambios se perderán)')) return;
    } else if (!window.confirm('¿Publicar la ruta? Las solicitudes pasarán a estado “Asignada” y se notificará a los ciudadanos.')) {
      return;
    }
    setActionInProgress('publish');
    try {
      await axios.patch(`${API}/admin/routes/${id}`, { status: 'Published' }, { headers: authHeaders() });
      toast.success('Ruta publicada');
      fetchRoute();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al publicar');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleComplete = async () => {
    if (!window.confirm('¿Marcar la ruta como completada?')) return;
    setActionInProgress('complete');
    try {
      await axios.patch(`${API}/admin/routes/${id}`, { status: 'Completed' }, { headers: authHeaders() });
      toast.success('Ruta marcada como completada');
      fetchRoute();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al completar');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar la ruta? Las solicitudes asociadas volverán a estado “Notificada”.')) return;
    setActionInProgress('delete');
    try {
      await axios.delete(`${API}/admin/routes/${id}`, { headers: authHeaders() });
      toast.success('Ruta eliminada');
      navigate('/admin/rutas');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
      setActionInProgress(null);
    }
  };

  const buildGmapsUrl = () => {
    const withCoords = stops.filter(s => s.latitude && s.longitude);
    if (withCoords.length === 0) return 'https://www.google.com/maps';
    if (withCoords.length === 1) {
      const s = withCoords[0];
      return `https://www.google.com/maps/search/?api=1&query=${s.latitude},${s.longitude}`;
    }
    const slice = withCoords.slice(0, 11);  // max 11 stops per Maps URL
    const origin = `${slice[0].latitude},${slice[0].longitude}`;
    const dest = `${slice[slice.length - 1].latitude},${slice[slice.length - 1].longitude}`;
    const wps = slice.slice(1, -1).map(s => `${s.latitude},${s.longitude}`).join('|');
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving${wps ? `&waypoints=${encodeURIComponent(wps)}` : ''}`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 size={20} className="animate-spin mr-2" /> Cargando ruta…
        </div>
      </AdminLayout>
    );
  }
  if (error) {
    return (
      <AdminLayout>
        <div className="max-w-md mx-auto mt-20 rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <AlertCircle size={28} className="mx-auto mb-3 text-red-600" />
          <p className="text-sm text-red-800 mb-4">{error}</p>
          <Link to="/admin/rutas" className="text-sm text-[#374B67] font-medium hover:underline">
            ← Volver al listado de rutas
          </Link>
        </div>
      </AdminLayout>
    );
  }
  if (!route) return null;

  const meta = STATUS_META[route.status] || STATUS_META.Draft;
  const metrics = route.metrics || {};
  const hasManyStops = stops.length > 11;

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <button
              onClick={() => navigate('/admin/rutas')}
              className="mt-1 p-2 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="route-detail-back"
              aria-label="Volver"
            >
              <ArrowLeft size={18} className="text-slate-600" />
            </button>
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h1 className="text-2xl font-semibold text-[#374B67]" data-testid="route-detail-name">
                  {route.name || 'Ruta'}
                </h1>
                <Badge className={`${meta.cls} border`}>{meta.label}</Badge>
                {route.type === 'IA' && (
                  <Badge variant="secondary" className="text-xs">
                    <Sparkles size={11} className="mr-1" /> IA
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-500 capitalize">{fmtDateES(route.date)}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="rounded-xl border-slate-200"
              data-testid="route-download-pdf"
            >
              {downloadingPdf
                ? <><Loader2 size={14} className="mr-2 animate-spin" /> Generando…</>
                : <><Download size={14} className="mr-2" /> PDF de ruta</>}
            </Button>
            <Button
              variant="outline"
              onClick={handleDownloadPoster}
              disabled={downloadingPoster}
              className="rounded-xl border-slate-200"
              data-testid="route-download-poster"
              title="Cartel A4 con QR grande para imprimir y colocar en la cabina del camión"
            >
              {downloadingPoster
                ? <><Loader2 size={14} className="mr-2 animate-spin" /> Generando…</>
                : <><Printer size={14} className="mr-2" /> Cartel QR</>}
            </Button>
            <a
              href={buildGmapsUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 rounded-xl border border-slate-200 text-sm hover:bg-slate-50 transition-colors"
              data-testid="route-open-gmaps"
            >
              <ExternalLink size={14} className="mr-2" /> Google Maps
            </a>
            {route.status === 'Draft' && (
              <Button
                onClick={handlePublish}
                disabled={actionInProgress === 'publish'}
                className="rounded-xl bg-[#009940] hover:bg-[#007F35]"
                data-testid="route-publish"
              >
                {actionInProgress === 'publish'
                  ? <><Loader2 size={14} className="mr-2 animate-spin" /> Publicando…</>
                  : <><CheckCircle2 size={14} className="mr-2" /> Publicar</>}
              </Button>
            )}
            {route.status === 'Published' && (
              <Button
                onClick={handleComplete}
                disabled={actionInProgress === 'complete'}
                className="rounded-xl bg-[#374B67] hover:bg-[#2A3B52] text-white"
                data-testid="route-complete"
              >
                {actionInProgress === 'complete'
                  ? <><Loader2 size={14} className="mr-2 animate-spin" /> Cerrando…</>
                  : <><CheckCircle2 size={14} className="mr-2" /> Completar</>}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={actionInProgress === 'delete'}
              className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
              data-testid="route-delete"
            >
              <Trash2 size={14} className="mr-2" /> Eliminar
            </Button>
          </div>
        </div>

        {/* Big-route notice (>11 stops) */}
        {hasManyStops && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-3">
            <QrCode size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <strong>Ruta de {stops.length} paradas.</strong> Google Maps soporta máx. 11 puntos por enlace.
              El PDF y el cartel se generan con <strong>varios QRs encadenados</strong> (cada tramo continúa donde acaba el anterior).
            </div>
          </div>
        )}

        {/* Info + metrics + map */}
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5">
          {/* Left: info card */}
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Información</p>
              <dl className="space-y-2.5 text-sm">
                <div className="flex items-start gap-2">
                  <Truck size={15} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <dt className="text-xs text-slate-500">Vehículo</dt>
                    <dd className="text-slate-800 font-medium truncate">
                      {route.vehicle_matricula || '—'}
                      {route.vehicle && (
                        <span className="text-xs text-slate-500 ml-2">
                          {[route.vehicle.marca, route.vehicle.modelo].filter(Boolean).join(' ')}
                        </span>
                      )}
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <User size={15} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <dt className="text-xs text-slate-500">Conductor</dt>
                    <dd className="text-slate-800">{route.driver_name || '—'}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock size={15} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <dt className="text-xs text-slate-500">Turno</dt>
                    <dd className="text-slate-800">{SHIFT_LABELS[route.shift] || route.shift || '—'}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar size={15} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <dt className="text-xs text-slate-500">Fecha</dt>
                    <dd className="text-slate-800 capitalize">{fmtDateES(route.date)}</dd>
                  </div>
                </div>
              </dl>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Métricas</p>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Paradas</p>
                  <p className="text-xl font-bold text-[#374B67] mt-0.5">{stops.length}</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Distancia</p>
                  <p className="text-xl font-bold text-[#374B67] mt-0.5">
                    {metrics.distance_km ?? '—'} <span className="text-xs font-medium">km</span>
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Tiempo</p>
                  <p className="text-xl font-bold text-[#374B67] mt-0.5">
                    {metrics.time_minutes ?? '—'} <span className="text-xs font-medium">min</span>
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Volumen</p>
                  <p className="text-xl font-bold text-[#374B67] mt-0.5">
                    {route.cluster_volume_m3 ?? '—'}<span className="text-xs font-medium">/{route.vehicle_capacity_m3 ?? '—'} m³</span>
                  </p>
                </div>
              </div>
            </div>

            {route.ai_notes && (
              <div className="rounded-xl border border-teal-200 bg-teal-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-teal-800 mb-2 flex items-center gap-1.5">
                  <Sparkles size={12} /> Notas IA
                </p>
                <p className="text-xs text-teal-900 whitespace-pre-wrap">{route.ai_notes}</p>
              </div>
            )}
          </div>

          {/* Right: map */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <RouteIcon size={16} className="text-[#374B67]" />
                <h2 className="text-sm font-semibold text-slate-800">Recorrido</h2>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#009940]" /> Inicio</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#374B67]" /> Paradas</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-600" /> Fin</span>
              </div>
            </div>
            <div
              ref={mapRef}
              className="w-full"
              style={{ height: 460 }}
              data-testid="route-detail-map"
            />
            {stops.length === 0 && (
              <div className="text-center py-6 text-sm text-slate-500">
                Esta ruta no tiene paradas con coordenadas.
              </div>
            )}
          </div>
        </div>

        {/* Stops list (sortable) */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Package size={16} className="text-[#374B67]" />
              <h2 className="text-sm font-semibold text-slate-800">
                Paradas <span className="text-slate-400">({stops.length})</span>
              </h2>
              {!dragDisabled && (
                <span className="text-xs text-slate-500 hidden sm:inline">
                  · Arrastra <GripVertical size={11} className="inline" /> para reordenar
                </span>
              )}
            </div>
            {orderChanged && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-amber-700 font-medium">Cambios sin guardar</span>
                <Button
                  variant="outline"
                  onClick={handleResetOrder}
                  className="rounded-lg border-slate-200 h-8 text-xs"
                  data-testid="route-reset-order"
                >
                  <Undo2 size={12} className="mr-1" /> Deshacer
                </Button>
                <Button
                  onClick={handleSaveOrder}
                  disabled={savingOrder}
                  className="rounded-lg bg-[#009940] hover:bg-[#007F35] h-8 text-xs"
                  data-testid="route-save-order"
                >
                  {savingOrder
                    ? <><Loader2 size={12} className="mr-1 animate-spin" /> Guardando…</>
                    : <><Save size={12} className="mr-1" /> Guardar orden</>}
                </Button>
              </div>
            )}
          </div>

          {stops.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">
              Esta ruta no tiene paradas asignadas.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={stops.map(s => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <table className="w-full text-sm" data-testid="route-stops-table">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-2 py-3 w-10"></th>
                        <th className="px-2 py-3 text-left w-12">#</th>
                        <th className="px-3 py-3 text-left">Ticket</th>
                        <th className="px-3 py-3 text-left">Ciudadano</th>
                        <th className="px-3 py-3 text-left">Contacto</th>
                        <th className="px-3 py-3 text-left">Dirección</th>
                        <th className="px-3 py-3 text-left">Enseres</th>
                        <th className="px-3 py-3 text-left">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stops.map((s, i) => (
                        <SortableStopRow
                          key={s.id}
                          stop={s}
                          index={i}
                          total={stops.length}
                          dragDisabled={dragDisabled}
                        />
                      ))}
                    </tbody>
                  </table>
                </SortableContext>
              </DndContext>
            </div>
          )}

          {dragDisabled && stops.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500 bg-slate-50/50">
              💡 El reordenamiento sólo está disponible en rutas en estado <strong>Borrador</strong>.
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
